import React, { useState, useEffect, useMemo } from 'react';
import CampaignStatsChart from './CampaignStatsChart';
import TreasureMap from './TreasureMap';
import { PeopleFill, HeartPulseFill, ChevronDown, ChevronRight, GeoAltFill } from 'react-bootstrap-icons';
import { getServerNow } from '../services/api';

const ItemsTable = ({ items = [], searchTerm, statusFilter }) => {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const normalizedItems = useMemo(() => {
    if (!items) return [];
    return items.map(item => ({
      itemname: item.itemname || item.itemName || '미정',
      rewardno: item.rewardno ?? item.rewardNo ?? '-',
      usable: item.usable,
      targetno: item.targetno ?? item.targetNo ?? item.lotvalue ?? item.lotValue,
      designatedHitNumber: item.designatedHitNumber ?? item.designatedhitnumber ?? item.designated_hit_number,
      isParticipationReward: item.isParticipationReward,
      status: item.status,
    }));
  }, [items]);

  const groupedItems = useMemo(() => {
    if (!normalizedItems || normalizedItems.length === 0) return [];
    const groups = normalizedItems.reduce((acc, item) => {
      acc[item.itemname] = acc[item.itemname] || { list: [], total: 0, given: 0, not_given: 0 };
      acc[item.itemname].list.push(item);
      acc[item.itemname].total++;
      if (item.usable === 0) {
        acc[item.itemname].given++;
      } else {
        acc[item.itemname].not_given++;
      }
      return acc;
    }, {});
    return Object.entries(groups).map(([name, data]) => ({ name, ...data }));
  }, [normalizedItems]);

  const filteredItems = useMemo(() => {
    return groupedItems
      .filter(group => group.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .filter(group => {
        if (statusFilter === 'given') return group.given > 0;
        if (statusFilter === 'not_given') return group.not_given > 0;
        return true;
      });
  }, [groupedItems, searchTerm, statusFilter]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredItems, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    setExpandedGroup(null); // Collapse on page change
  };

  const toggleGroup = (groupName) => {
    setExpandedGroup(expandedGroup === groupName ? null : groupName);
  };

  if (normalizedItems.length === 0) {
    return <div className="alert alert-info mt-2">표시할 상품이 없습니다.</div>;
  }

  return (
    <div>
      <table className="table table-striped table-bordered table-hover mt-2">
        <thead className="table-dark">
          <tr>
            <th></th>
            <th>상품명</th>
            <th>총 수량</th>
            <th>지급 완료</th>
            <th>미지급</th>
          </tr>
        </thead>
        <tbody>
          {paginatedItems.map(group => (
            <React.Fragment key={group.name}>
              <tr onClick={() => toggleGroup(group.name)} style={{ cursor: 'pointer' }}>
                <td>
                  {expandedGroup === group.name ? <ChevronDown /> : <ChevronRight />}
                </td>
                <td>{group.name}</td>
                <td>{group.total}</td>
                <td>{group.given}</td>
                <td>{group.not_given}</td>
              </tr>
              {expandedGroup === group.name && (
                <tr>
                  <td colSpan="5">
                    <div style={{ padding: '1rem' }}>
                      <h5>{group.name} 상세 목록</h5>
                      <table className="table table-sm table-bordered">
                        <thead className="table-secondary">
                          <tr>
                            <th>순위</th>
                            <th>지정 타수</th>
                            <th>개별 상태</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.list.map(item => {
                            const rawHit = item.designatedHitNumber ?? item.targetno ?? item.targetNo ?? item.lotvalue ?? item.lotValue;
                            const isParticipation = item.isParticipationReward === true || rawHit === null || rawHit === undefined || Number(rawHit) === 0;
                            const displayHit = isParticipation ? '참가상' : rawHit;
                            return (
                              <tr key={item.rewardno}>
                                <td>{item.rewardno}</td>
                                <td>{displayHit}</td>
                                <td>{item.usable === 0 ? '지급 완료' : '미지급'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <nav className="d-flex justify-content-center">
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>이전</button>
            </li>
            {[...Array(totalPages).keys()].map(num => (
              <li key={num + 1} className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => handlePageChange(num + 1)}>{num + 1}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>다음</button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

const RankedGourdBreakingReport = ({ rankedBoxEvents, luckyBoxEvents, totalSignups }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'given', 'not_given'
  // 자동 파괴 시각과 비교할 수 있도록 매초 갱신되는 서버 기준 현재 시각
  const [now, setNow] = useState(() => getServerNow());

  useEffect(() => {
    const timer = setInterval(() => setNow(getServerNow()), 1000);
    return () => clearInterval(timer);
  }, []);

  const events = rankedBoxEvents && rankedBoxEvents.length > 0 ? rankedBoxEvents : (luckyBoxEvents || []);

  if (!events || events.length === 0) {
    return <p>표시할 박 터트리기 이벤트가 없습니다.</p>;
  }

  return (
    <div>
      {events.map(event => {
        const getProgressBarVariant = (percentage) => {
          if (percentage > 50) return 'bg-success';
          if (percentage > 20) return 'bg-warning';
          return 'bg-danger';
        };

        return (
          <div key={event.eventKey} className="card mb-4 shadow-sm">
            <div className="card-header bg-primary text-white">
              <h3 className="mb-0">{event.eventName} ({event.statusName})</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-7 d-flex">
                  <div className="card shadow-sm flex-fill">
                    <div className="card-header">
                      <h4 className="mb-0"><PeopleFill className="me-2" />참여 현황</h4>
                    </div>
                    <div className="card-body">
                      <CampaignStatsChart
                        signupCount={totalSignups}
                        participantCount={event.participantCount}
                        todaySignups={event.todaySignups || 0}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-5">
                  <h4 className="mb-3"><HeartPulseFill className="me-2" />박 HP 정보</h4>
                  {event.baskets && event.baskets.map(basket => {
                    const basketHpPercent = basket && basket.maxHP > 0
                      ? (basket.currentHP / basket.maxHP) * 100
                      : 0;

                    return (
                      <div key={basket.basketKey} className="card mb-3">
                        <div className="card-body">
                          <h5 className="card-title">{basket.basketName}</h5>
                          <div className="progress" style={{ height: '25px', fontSize: '1rem' }}>
                            <div
                              className={`progress-bar ${getProgressBarVariant(basketHpPercent)}`}
                              role="progressbar"
                              style={{ width: `${basketHpPercent}%` }}
                              aria-valuenow={basketHpPercent}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            >
                              {basketHpPercent.toFixed(2)}%
                            </div>
                          </div>
                          <p className="text-muted mt-2 mb-1">
                            {basket.currentHP.toLocaleString()} / {basket.maxHP.toLocaleString()}
                          </p>
                          <p className="text-muted small mb-1">현재 시각: {now.toLocaleString()}</p>
                          {basket.killdt && <p className="text-dark mb-0"><strong>자동 파괴:</strong> {new Date(basket.killdt).toLocaleString()}</p>}
                          {basket.status === 1 && <p className="text-dark mb-0"><strong>상태:</strong> 아직 터지지 않음</p>}
                          {basket.damageDealt !== undefined && <p className="text-dark mb-0"><strong>공격 횟수:</strong> {basket.damageDealt.toLocaleString()} 회</p>}
                          {basket.latitude && basket.longitude && <p className="text-dark mb-0"><GeoAltFill className="me-2" /><strong>위치:</strong> {basket.latitude}, {basket.longitude}</p>}
                        </div>
                      </div>
                    );
                  })}
                  <div className="card mb-3">
                    <div className="card-header">
                      <h5 className="mb-0">이벤트 요약</h5>
                    </div>
                    <div className="card-body">
                      <p className="card-text"><PeopleFill className="me-2" /><strong>참가자:</strong> {event.participantCount} 명</p>
                      <p className="card-text"><PeopleFill className="me-2" /><strong>오늘 가입자:</strong> {event.todaySignups || 0} 명</p>
                      <p className="card-text"><PeopleFill className="me-2" /><strong>브랜드 노출 횟수:</strong> {event.brandExposure} 회</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-12">
                  <h5 className="mb-3 fw-bold">박 위치</h5>
                  <div style={{ height: '200px', width: '100%' }}>
                    <TreasureMap markers={event.baskets.map(b => ({ latitude: b.latitude, longitude: b.longitude, boxName: b.basketName }))} />
                  </div>
                </div>
              </div>

              <h4 className="mt-4">상품 목록</h4>
              <p className="text-muted">지정된 타수(번호)에 도달한 참여자에게 해당 상품이 지급됩니다.</p>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="상품명 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">전체 보기</option>
                    <option value="not_given">미지급만 보기</option>
                    <option value="given">지급 완료만 보기</option>
                  </select>
                </div>
              </div>
              <ItemsTable items={event.items || event.rewards || []} searchTerm={searchTerm} statusFilter={statusFilter} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RankedGourdBreakingReport;
